// Web mock for react-native-qrcode-svg
import React from 'react';

function flattenStyle(style) {
  if (Array.isArray(style)) {
    return Object.assign({}, ...style.filter(Boolean));
  }
  return style || {};
}
import QRCodeLib from 'qrcode';

const QRCode = ({ 
  value = '', 
  size = 100, 
  color = '#000000', 
  backgroundColor = '#FFFFFF',
  logo,
  logoSize = 20,
  logoBackgroundColor = backgroundColor,
  logoMargin = 2,
  logoBorderRadius = 0,
  quietZone = 0,
  enableLinearGradient = false,
  linearGradient = ['#000000', '#FFFFFF'],
  gradientDirection = ['0%', '0%', '100%', '100%'],
  ecl = 'M', // Error correction level
  getRef,
  onError,
  ...props 
}) => {
  const canvasRef = React.useRef(null);

  React.useEffect(() => {
    if (!canvasRef.current || !value) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Clear canvas
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, size, size);

    try {
      // Use proper QR code library
      QRCodeLib.toCanvas(canvas, value, {
        width: size,
        color: {
          dark: color,
          light: backgroundColor,
        },
        errorCorrectionLevel: ecl,
      }).then(() => {
        // Add logo if provided
        if (logo) {
          drawLogo(ctx, logo, size, logoSize, logoBackgroundColor, logoMargin, logoBorderRadius);
        }
      }).catch(error => {
        console.warn('QR Code generation failed:', error);
        if (onError) onError(error);
        drawFallbackPattern(ctx, size, color, backgroundColor);
      });
    } catch (error) {
      console.warn('QR Code generation failed:', error);
      if (onError) onError(error);
      
      // Fallback: draw a simple pattern
      drawFallbackPattern(ctx, size, color, backgroundColor);
    }

    if (getRef) {
      getRef(canvas);
    }
  }, [value, size, color, backgroundColor, logo, logoSize]);

  const drawSimpleQRPattern = (ctx, size, color) => {
    ctx.fillStyle = color;
    const moduleSize = size / 25; // 25x25 grid for simplicity
    
    // Draw a simple pattern that looks like a QR code
    for (let row = 0; row < 25; row++) {
      for (let col = 0; col < 25; col++) {
        // Create a pseudo-random pattern based on the value
        const hash = simpleHash(value + row + col);
        if (hash % 3 === 0) {
          ctx.fillRect(col * moduleSize, row * moduleSize, moduleSize, moduleSize);
        }
      }
    }
    
    // Draw corner squares (finder patterns)
    drawFinderPattern(ctx, 0, 0, moduleSize);
    drawFinderPattern(ctx, 18 * moduleSize, 0, moduleSize);
    drawFinderPattern(ctx, 0, 18 * moduleSize, moduleSize);
  };

  const drawFinderPattern = (ctx, x, y, moduleSize) => {
    // 7x7 finder pattern
    ctx.fillRect(x, y, 7 * moduleSize, 7 * moduleSize);
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(x + moduleSize, y + moduleSize, 5 * moduleSize, 5 * moduleSize);
    ctx.fillStyle = color;
    ctx.fillRect(x + 2 * moduleSize, y + 2 * moduleSize, 3 * moduleSize, 3 * moduleSize);
  };

  const drawLogo = (ctx, logo, size, logoSize, bgColor, margin, borderRadius) => {
    const logoX = (size - logoSize) / 2;
    const logoY = (size - logoSize) / 2;
    
    // Draw logo background
    ctx.fillStyle = bgColor;
    if (borderRadius > 0) {
      drawRoundedRect(ctx, logoX - margin, logoY - margin, logoSize + 2 * margin, logoSize + 2 * margin, borderRadius);
    } else {
      ctx.fillRect(logoX - margin, logoY - margin, logoSize + 2 * margin, logoSize + 2 * margin);
    }
    
    // Note: In Phase 4, we'd properly load and draw the logo image
    // For now, just draw a placeholder
    ctx.fillStyle = '#666666';
    ctx.fillRect(logoX, logoY, logoSize, logoSize);
  };

  const drawRoundedRect = (ctx, x, y, width, height, radius) => {
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
    ctx.fill();
  };

  const drawFallbackPattern = (ctx, size, color, bgColor) => {
    // Draw a simple text fallback
    ctx.fillStyle = color;
    ctx.font = `${size / 8}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('QR Code', size / 2, size / 2);
    ctx.fillText(value.substring(0, 10) + '...', size / 2, size / 2 + size / 6);
  };

  const simpleHash = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash);
  };

  return React.createElement('canvas', {
    ref: canvasRef,
    width: size,
    height: size,
    style: { 
      width: size, 
      height: size,
      border: quietZone > 0 ? `${quietZone}px solid ${backgroundColor}` : 'none'
    , ...flattenStyle(props?.style) },
    ...props
  });
};

export default QRCode;
